import { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import toast from 'react-hot-toast';
import axios from 'axios';
import { API_CONSTANTS } from '../../../config/api-constants';
import { formatPhoneNumberDashes } from '../../../utils/services';

const getStorageItem = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const AddAgentDialog = ({
  open,
  onOpenChange,
  onAgentAdded,
  initialData = {},
  agents = [],
  companyKey = "",
}) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    agency: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const emailRef = useRef(null);
  const debounceRef = useRef(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [existingAgent, setExistingAgent] = useState(null);
  const [requiresNewEmail, setRequiresNewEmail] = useState(false);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (formData.phone.trim() && !/^\d{3}-\d{3}-\d{4}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Phone number must be in 123-456-7890 format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkExistingEmail = (email) => {
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setExistingAgent(null);
      return;
    }
    setIsCheckingEmail(true);
    try{
    const match = agents.find(
      (a) => (a.email || '').toLowerCase() === trimmed.toLowerCase()
    );
    setExistingAgent(match || null);
  }
  finally{
    setIsCheckingEmail(false);
  }
  };

  const handleSubmit = async () => {
    if (requiresNewEmail) return;
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        company_key: companyKey || getStorageItem(sessionStorage, 'company_key') || getStorageItem(localStorage, 'scheduler_company_key') || '',
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        agency: formData.agency.trim(),
      };
      const response = await axios.post(API_CONSTANTS.ADD_ONLINE_SCHEDULER_AGENTS, payload);
      if (response?.data?.success) {
        const returnedId = response.data?.data;
        const newAgent = {
          id: returnedId || `agent-new-${Date.now()}`,
          first_name: payload.first_name,
          last_name: payload.last_name,
          email: payload.email,
          phone: payload.phone,
          agency: payload.agency,
          agentdata: { agency: payload.agency },
        };
        toast.success('Agent added successfully');
        onAgentAdded(newAgent);
        handleClose();
      } else {
        const msg = response?.data?.message || 'Failed to add agent';
        setSubmitError(msg);
      }
    } catch (error) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message || 'Failed to add agent';
      console.error('Error adding agent', { status, message });
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      agency: '',
    });
    setErrors({});
    setExistingAgent(null);
    setRequiresNewEmail(false);
    setSubmitError('');
    onOpenChange(false);
  };

  const handleUseExisting = () => {
    if (!existingAgent) return;
    onAgentAdded(existingAgent);
    handleClose();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
    if (field === 'email') {
      setExistingAgent(null);
      if (requiresNewEmail) setRequiresNewEmail(false);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => checkExistingEmail(value), 600);
    }
    if (submitError) setSubmitError('');
  };

  const inputClassName =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#239D80] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm border-solid';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px] add-agent-dialog max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Agent</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email*
            </label>
            <input
              id="email"
              ref={emailRef}
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={(e) => checkExistingEmail(e.target.value)}
              placeholder="john.doe@agency.com"
              className={inputClassName}
            />
            {existingAgent && (
              <p className="mt-2 px-[5px] text-[#ec2506] my-0 text-xs font-medium">Existing agent found.</p>
            )}
            {!requiresNewEmail && isCheckingEmail && (
              <p className="mt-1 px-[5px] text-gray-500 my-0 text-xs">
                Checking for existing agent...
              </p>
            )}
          </div>

          {existingAgent && (
            <div className="rounded-md border border-[#239D80] bg-[#239D80]/5 p-3">
              <p className="text-sm text-gray-600 mb-2">
                We found an agent with this email. Review the details below and choose how you'd like to continue.
              </p>
              <div className="text-sm font-medium text-gray-900">
                {existingAgent.first_name} {existingAgent.last_name}
              </div>
              <div className="text-xs text-gray-600 mb-3">
                {existingAgent.agentdata?.agency || existingAgent.agency || existingAgent.email} · {existingAgent.phone}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleUseExisting}
                  className="border-none bg-[#239D80] hover:bg-[#239D80]/90"
                >
                  Use Existing Agent
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setExistingAgent(null);
                    setRequiresNewEmail(false);
                    setFormData((prev) => ({
                      ...prev,
                      email: '',
                      first_name: '',
                      last_name: '',
                      phone: '',
                      agency: '',
                    }));
                    setTimeout(() => {
                      emailRef.current?.focus();
                      emailRef.current?.select();
                    }, 0);
                  }}
                  className="border-[#239D80] text-[#239D80] hover:bg-[#239D80]/10 hover:text-[#239D80]"
                >
                  Use a Different Email
                </Button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name*
              </label>
              <input
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                placeholder="John"
                className={inputClassName}
              />
              {errors.first_name && (
                <p className="mt-1 px-[5px] text-[#ec2506] my-0 text-xs">
                  {errors.first_name}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name*
              </label>
              <input
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                placeholder="Doe"
                className={inputClassName}
              />
              {errors.last_name && (
                <p className="mt-1 px-[5px] text-[#ec2506] my-0 text-xs">
                  {errors.last_name}
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={12}
              value={formData.phone}
              onChange={(e) => handleChange('phone', formatPhoneNumberDashes(e.target.value))}
              placeholder="123-456-7890"
              className={inputClassName}
            />
            {errors.phone && (
              <p className="mt-1 px-[5px] text-[#ec2506] my-0 text-xs">
                {errors.phone}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Agency
            </label>
            <input
              value={formData.agency}
              onChange={(e) => handleChange('agency', e.target.value)}
              placeholder="ABC Real Estate"
              className={inputClassName}
            />
            {errors.agency && (
              <p className="mt-1 px-[5px] text-[#ec2506] my-0 text-xs">
                {errors.agency}
              </p>
            )}
          </div>
        </div>

        {submitError && (
          <p className="px-[5px] text-[#ec2506] text-sm mb-2">{submitError}</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting} className="border-[#239D80] text-[#239D80] hover:bg-[#239D80]/10 hover:text-[#239D80]">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !!existingAgent} className="border-none bg-[#239D80] hover:bg-[#239D80]/90">
            {isSubmitting ? 'Saving...' : 'Save Agent'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddAgentDialog;